import { type MouseEvent, type ReactNode, useState } from 'react';
import concatClassNames from 'utils/classNames';
import './Card.scss';
import CloudinaryImage from '../common/cloudinary-img/CloudinaryImage';

export interface ICardProps {
  children: ReactNode
  title: string | ReactNode
  imgSrc?: string
  altText?: string
  icon?: any
  className?: string
  onClick?: () => void
  onIconClick?: () => void
  titleClassName?: string
  isCloudinaryImage?: boolean
  // Level of the card title: 2 when the cards sit directly under the page h1 (listings), 3 inside a section that has its own h2.
  headingLevel?: 2 | 3 | 4
}

const Card = ({
  icon: IconComponent,
  children,
  imgSrc,
  title,
  className,
  onClick,
  altText,
  onIconClick,
  titleClassName,
  isCloudinaryImage,
  headingLevel = 3
}: ICardProps) => {
  const Heading = `h${headingLevel}` as const;
  const [isIconActive, setIsIconActive] = useState(false);

  const cardClasses = `flex flex-column card ${onClick ? 'clickable' : ''} ${className ?? ''}`;

  const handleIconClick = (e: MouseEvent) => {
    e.preventDefault();
    setIsIconActive(!isIconActive);
    onIconClick && onIconClick();
  };

  return (
    <article className={cardClasses} onClick={onClick}>
      {IconComponent && (
        <IconComponent
          className={concatClassNames('card-icon', isIconActive ? 'active' : '')}
          onClick={handleIconClick}
        />
      )}
      {isCloudinaryImage
        ? <CloudinaryImage imagePublicId={imgSrc ?? ''} className="card-image"/>
        : <img src={imgSrc} alt={altText ?? ''} className="card-image"/>}
      <div className="flex flex-column card-content">
        <Heading className={concatClassNames('card-content-title', titleClassName)}>
          {title}
        </Heading>
        {children}
      </div>
    </article>
  );
};

export default Card;
